import React from 'react';
import { Checkbox, Fieldset, Tag, TagGroup } from '@flixbus/honeycomb-react';
import { useVoucherFormContext } from '../../Context';
import { AllowedTransportTypeRule, TransportType } from '../../../../types/Rules';
import styles from '../../voucher-form.module.scss';

const ALL_TRANSPORT_TYPES: TransportType[] = ['bus', 'train', 'ferry', 'mini_bus'];

const TRANSPORT_OPTIONS: { label: string; value: TransportType }[] = [
  { label: 'Bus', value: 'bus' },
  { label: 'Train', value: 'train' },
  { label: 'Ferry', value: 'ferry' },
  { label: 'Mini Bus', value: 'mini_bus' },
];

const TransportTypeRule: React.FC = () => {
  const {
    formState: {
      payload: { rules },
    },
    handleRuleChange,
  } = useVoucherFormContext();

  const currentRule = rules.find(r => r.type === 'allowed_transport_type') as
    | AllowedTransportTypeRule
    | undefined;

  const selected = new Set(currentRule?.transportTypes ?? []);

  // No rule means all transport types are allowed (default behaviour)
  const isAllSelected = !currentRule || ALL_TRANSPORT_TYPES.every(t => selected.has(t));

  const updateRule = (next: Set<TransportType>) => {
    const removeRule = next.size === 0 || ALL_TRANSPORT_TYPES.every(t => next.has(t));
    handleRuleChange(
      { type: 'allowed_transport_type', transportTypes: Array.from(next) },
      removeRule,
    );
  };

  const handleToggle = (value: TransportType) => {
    const base = isAllSelected ? new Set(ALL_TRANSPORT_TYPES) : new Set(selected);
    if (base.has(value)) {
      base.delete(value);
    } else {
      base.add(value);
    }
    updateRule(base);
  };

  const handleToggleAll = () => {
    if (!isAllSelected) {
      // Selecting all = default behaviour = remove the rule
      updateRule(new Set());
    }
  };

  const visibleTags = isAllSelected
    ? TRANSPORT_OPTIONS
    : TRANSPORT_OPTIONS.filter(({ value }) => selected.has(value));

  return (
    <>
      <Fieldset legend='Allowed transport types' horizontal extraClasses={styles.choiceWrapper}>
        <Checkbox
          id='transport-type-all'
          label='All'
          value='all'
          checked={isAllSelected}
          onChange={handleToggleAll}
        />
        {TRANSPORT_OPTIONS.map(({ label, value }) => (
          <Checkbox
            key={value}
            id={`transport-type-${value}`}
            label={label}
            value={value}
            checked={isAllSelected || selected.has(value)}
            onChange={() => handleToggle(value)}
          />
        ))}
      </Fieldset>
      {visibleTags.length > 0 && (
        <TagGroup>
          {visibleTags.map(({ label, value }) => (
            <Tag
              key={value}
              closeProps={{
                onClick: () => handleToggle(value),
                label: `Remove ${label}`,
              }}
            >
              {label}
            </Tag>
          ))}
        </TagGroup>
      )}
    </>
  );
};

export default TransportTypeRule;
